import { useEffect, useLayoutEffect, useState } from 'react';
import type { Services } from '@packages/shared/core/common/context';
import {
  FeatureFlagsConfigLoadedEvent,
  FeatureFlagsConfigLoadingFailedEvent,
} from '@packages/shared/core/feature-flags/events';
import { fetchLocationRequest } from '@packages/shared/core/location/store';
import { createStore, type AppDispatch } from '@packages/shared/core/common/store';
import { LoadingStatuses } from '../entities';
import { useFeatureFlagsLoader } from './useFeatureFlagsLoader';
import { useFeatureFlagsListeners } from './useFeatureFlagsListeners';

export interface UseAppInitializationResult {
  isLoaderVisible: boolean;
  featureFlagsLoadingStatus: (typeof LoadingStatuses)[keyof typeof LoadingStatuses];
}

export const useAppInitialization = (
  services: Services,
  store: ReturnType<typeof createStore>,
): UseAppInitializationResult => {
  const [featureFlagsLoadingStatus, setFeatureFlagsLoadingStatus] = useState(LoadingStatuses.PENDING);
  const dispatch: AppDispatch = store.dispatch;

  const featureFlags = services.featureFlags;
  const analyticsService = services.analyticsService;
  const arrivalService = services.arrivalService;
  const logger = services.logger;
  const clarityHeatmapRecordingService = services.clarityHeatmapRecordingService;
  const cookieConsentService = services.cookieConsentService;
  const errorMonitoringService = services.errorMonitoringService;

  if (!featureFlags || !analyticsService || !logger || !cookieConsentService) {
    throw new Error('Required services are missing');
  }

  const { isLoaderVisible, hideLoader } = useFeatureFlagsLoader(logger);

  useLayoutEffect(() => {
    arrivalService.trackArrival();
  }, [arrivalService]);

  useEffect(() => {
    cookieConsentService.initialize();
  }, [cookieConsentService]);

  useFeatureFlagsListeners(featureFlags, {
    onLoaded: (features) => {
      hideLoader();
      setFeatureFlagsLoadingStatus(LoadingStatuses.FULFILLED);
      analyticsService.track(new FeatureFlagsConfigLoadedEvent({ config: features }));
    },
    onFailed: (error) => {
      hideLoader();
      setFeatureFlagsLoadingStatus(LoadingStatuses.FAILED);
      logger.error(error);
      analyticsService.track(new FeatureFlagsConfigLoadingFailedEvent({ error }));
    },
  });

  useLayoutEffect(() => {
    analyticsService.launch();
  }, [analyticsService]);

  useLayoutEffect(() => {
    const identifyUser = async () => {
      try {
        const deviceId = await analyticsService.getDeviceId();
        if (errorMonitoringService) {
          errorMonitoringService.identify(deviceId);
        }
      } catch {}
    };

    identifyUser();
  }, [analyticsService, errorMonitoringService]);

  useEffect(() => {
    dispatch(fetchLocationRequest());
  }, [dispatch]);

  useEffect(() => {
    if (clarityHeatmapRecordingService) {
      clarityHeatmapRecordingService.initialize();
    }
  }, [clarityHeatmapRecordingService]);

  useEffect(() => {
    featureFlags.onAttributesUpdated((attributes) => {
      analyticsService.setFeatureFlagsAttributes(attributes);

      if (attributes.id) {
        analyticsService.addUserAssociatedId(attributes.id);
      }
    });
  }, [analyticsService, featureFlags]);

  return {
    isLoaderVisible,
    featureFlagsLoadingStatus,
  };
};
